import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Button, Typography, List, Avatar, message } from 'antd';
import { 
  FileTextOutlined, 
  UserOutlined, 
  BookOutlined, 
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getTeacherStats, getExercises, getAssignments } from '../../api/exercises';

const { Title, Text } = Typography;

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalExercises: 0,
    totalStudents: 0,
    totalQuestions: 0,
    completedSubmissions: 0
  });
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [upcomingExercises, setUpcomingExercises] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 获取教师统计数据
        const statsData = await getTeacherStats();
        setStats({
          totalExercises: statsData.totalExercises || 0,
          totalStudents: statsData.totalStudents || 0,
          totalQuestions: statsData.totalQuestions || 0,
          completedSubmissions: statsData.completedSubmissions || 0
        });

        // 获取最近的提交记录
        const submissionsData = await getAssignments({ 
          limit: 5, 
          sortBy: 'created_at', 
          sortOrder: 'desc',
          withSubmissions: true
        });
        
        if (submissionsData && submissionsData.assignments) {
          const formattedSubmissions = submissionsData.assignments
            .filter(a => a.submissions && a.submissions.length > 0)
            .map(a => ({
              id: a.submissions[0].id,
              studentName: a.student.name,
              exercise: a.exercise.title,
              score: a.submissions[0].score || 0,
              submittedAt: new Date(a.submissions[0].submitted_at).toLocaleString()
            }))
            .slice(0, 4);
          
          setRecentSubmissions(formattedSubmissions);
        }

        // 获取即将到期的练习
        const now = new Date();
        const assignmentsData = await getAssignments({
          active: true,
          sortBy: 'end_time',
          sortOrder: 'asc'
        });
        
        if (assignmentsData && assignmentsData.assignments) {
          // 对作业按练习ID分组，统计每个练习分配给了多少学生
          const exerciseMap = new Map();
          
          assignmentsData.assignments.forEach(a => {
            if (new Date(a.end_time) > now) {
              if (!exerciseMap.has(a.exercise.id)) {
                exerciseMap.set(a.exercise.id, {
                  id: a.exercise.id,
                  title: a.exercise.title,
                  dueDate: new Date(a.end_time).toLocaleDateString(),
                  students: 1
                });
              } else {
                const exercise = exerciseMap.get(a.exercise.id);
                exercise.students += 1;
              }
            }
          });
          
          // 转换为数组并按截止日期排序
          const upcomingData = Array.from(exerciseMap.values())
            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
            .slice(0, 3);
          
          setUpcomingExercises(upcomingData);
        }
      } catch (error) {
        console.error('获取仪表盘数据失败:', error);
        message.error('获取数据失败，请刷新页面重试');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const submissionsColumns = [
    {
      title: '学生',
      dataIndex: 'studentName',
      key: 'studentName',
    },
    {
      title: '练习',
      dataIndex: 'exercise',
      key: 'exercise',
    },
    {
      title: '分数',
      dataIndex: 'score',
      key: 'score',
      render: (score) => (
        <span style={{ color: score >= 90 ? '#52c41a' : score >= 60 ? '#1890ff' : '#f5222d' }}>
          {score}
        </span>
      )
    },
    {
      title: '提交时间',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button type="link" size="small">
          查看详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>教师仪表盘</Title>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="练习总数"
              value={stats.totalExercises}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="学生总数"
              value={stats.totalStudents}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="题目总数"
              value={stats.totalQuestions}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="已完成提交"
              value={stats.completedSubmissions}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 最近提交 */}
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>最近提交</span>
                <Button type="link" size="small">查看全部</Button>
              </div>
            }
            loading={loading}
          >
            <Table 
              columns={submissionsColumns} 
              dataSource={recentSubmissions} 
              pagination={false}
              rowKey="id"
              locale={{ emptyText: '暂无提交记录' }}
            />
          </Card>
        </Col>
        
        {/* 即将到期的练习 */}
        <Col xs={24} lg={8}>
          <Card 
            title="即将到期的练习" 
            loading={loading}
            extra={<Button type="link" size="small" onClick={() => navigate('/teacher/exercises')}>全部练习</Button>}
          >
            <List
              dataSource={upcomingExercises}
              locale={{ emptyText: '暂无即将到期的练习' }}
              renderItem={item => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar icon={<ClockCircleOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                    title={<a onClick={() => navigate(`/teacher/exercises/${item.id}`)}>{item.title}</a>}
                    description={`截止日期: ${item.dueDate} · ${item.students} 名学生`}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TeacherDashboard;  
